import { forwardRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoMdNotificationsOutline } from "react-icons/io";
import {
  MdOutlineSubscriptions,
  MdSystemUpdateAlt,
  MdCampaign,
  MdPayment,
  MdAdsClick,
  MdCardGiftcard,
} from "react-icons/md"; 
import { FaBlog, FaRocket } from "react-icons/fa";
import { CiUser } from "react-icons/ci";
import { LuUsers } from "react-icons/lu";
import { MdOutlineDashboard } from "react-icons/md";
import { FaUserFriends } from "react-icons/fa";
import { HiUsers } from "react-icons/hi2";
import { FiUserPlus, FiX } from "react-icons/fi";
import { MdOutlineAdsClick } from "react-icons/md";
import { MdOutlineFlag, MdOutlineBlock, MdOutlineSupportAgent, MdOutlineAttachMoney, MdOutlineChat, MdDevices } from "react-icons/md";
import { SIDEBAR_TABS } from "../constants/sidebarTabs";
import supportService from "../api/services/supportService";
import {
  CHAT_SUPPORT_ASSIGNED_TAB_KEYS,
  LEGACY_WORKSPACE_SIDEBAR_TABS,
  NORMAL_CHAT_SUPPORT_SIDEBAR_TABS,
  NORMAL_CSR_LEAD_SIDEBAR_TABS,
  NORMAL_CSR_SIDEBAR_TABS,
  readSupportHasTeam,
  readSupportTabPermissions,
  SIDEBAR_TAB_PERM_KEYS,
  storeSupportAccessMeta,
} from "../constants/supportTabPermissions";
import {
  canAccessAgentDesk,
  canAccessTeamHub,
} from "../Screens/Workspace/workspaceConfig";
import { SidebarSkeleton } from "../components/common/skeletons";
import usePendingApprovalsCount from "../hooks/usePendingApprovalsCount";
import useAssignedChatUnreadCount from "../hooks/useAssignedChatUnreadCount";
import useUnclaimedSupportCount from "../hooks/useUnclaimedSupportCount";
import { HiOutlineViewGrid } from "react-icons/hi";
import { MdGroups } from "react-icons/md";

const SUPPORT_ROLES_WITH_TAB_PERMS = new Set([
  "CSR",
  "CSR Team Lead",
  "Chat Support",
]);

const Sidebar = forwardRef(({ onClose }, ref) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [tabPerms, setTabPerms] = useState(() => readSupportTabPermissions());
  const [hasTeam, setHasTeam] = useState(() => readSupportHasTeam());
  // Support roles wait for first tab-permissions fetch; Admin/Dev are ready immediately.
  const [accessReady, setAccessReady] = useState(() => {
    const role = localStorage.getItem("adminUserRole") || "Admin";
    return !SUPPORT_ROLES_WITH_TAB_PERMS.has(role);
  });

  // Initialize on client side
  useEffect(() => {
    setIsClient(true);
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 770);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Get user role from localStorage
  const userRole = localStorage.getItem("adminUserRole") || "Admin";

  useEffect(() => {
    if (!SUPPORT_ROLES_WITH_TAB_PERMS.has(userRole)) {
      setAccessReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await supportService.getAccess();
        const access = data.access || null;
        const perms = access?.tabPermissions || null;
        if (cancelled) return;
        if (access) {
          storeSupportAccessMeta(access);
          setHasTeam(Boolean(access.hasTeam));
        }
        if (perms) {
          setTabPerms(perms);
        }
      } catch {
        if (!cancelled) {
          setTabPerms(readSupportTabPermissions());
          setHasTeam(readSupportHasTeam());
        }
      } finally {
        if (!cancelled) setAccessReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userRole]);

  const permAllows = (key, fallback = true) => {
    if (!tabPerms || typeof tabPerms !== "object") return fallback;
    if (typeof tabPerms[key] !== "boolean") return fallback;
    return tabPerms[key];
  };

  const usesTeamTabRestrictions =
    SUPPORT_ROLES_WITH_TAB_PERMS.has(userRole) && hasTeam === true;

  const isTeamAssignedSidebarTab = (tabName) =>
    Object.prototype.hasOwnProperty.call(SIDEBAR_TAB_PERM_KEYS, tabName);

  const canSeeSupportRoleTab = (tabName) => {
    if (tabName === SIDEBAR_TABS.DASHBOARD) return true;

    /** Support Center, CSR Outreach, Agent Reports, etc. — team-assigned tabs. */
    if (isTeamAssignedSidebarTab(tabName)) {
      const permKey = SIDEBAR_TAB_PERM_KEYS[tabName];
      if (userRole === "Chat Support") {
        if (!CHAT_SUPPORT_ASSIGNED_TAB_KEYS.includes(permKey)) return false;
        return permAllows(permKey, false);
      }
      if (!usesTeamTabRestrictions) return false;
      return permAllows(permKey, false);
    }

    if (userRole === "Chat Support") {
      return NORMAL_CHAT_SUPPORT_SIDEBAR_TABS.includes(tabName);
    }
    if (userRole === "CSR Team Lead") {
      return NORMAL_CSR_LEAD_SIDEBAR_TABS.includes(tabName);
    }
    if (userRole === "CSR") {
      return NORMAL_CSR_SIDEBAR_TABS.includes(tabName);
    }
    return false;
  };

  // Define which tabs each role can see
  const canSeeTab = (tabName) => {
    // Collapsed into My Desk / Team Hub — keep routes, hide from sidebar
    if (LEGACY_WORKSPACE_SIDEBAR_TABS.has(tabName)) return false;

    if (tabName === SIDEBAR_TABS.AGENT_DESK) {
      return canAccessAgentDesk(userRole);
    }
    if (tabName === SIDEBAR_TABS.TEAM_HUB) {
      return canAccessTeamHub(userRole);
    }

    // Manual Purchase: only Developer
    if (tabName === SIDEBAR_TABS.SUPPORT_TEAMS) {
      if (!["Admin", "Developer", "CSR Team Lead"].includes(userRole)) return false;
      if (userRole === "CSR Team Lead") {
        return usesTeamTabRestrictions ? permAllows("teams", false) : true;
      }
      return true;
    }
    if (tabName === SIDEBAR_TABS.CSR_OUTREACH) {
      if (!["Admin", "Developer", "CSR", "CSR Team Lead"].includes(userRole)) return false;
      if (userRole === "CSR" || userRole === "CSR Team Lead") {
        return canSeeSupportRoleTab(tabName);
      }
      return true;
    }
    if (tabName === SIDEBAR_TABS.SALE_AGENT) {
      if (!["Admin", "Developer", "CSR", "CSR Team Lead"].includes(userRole)) return false;
      if (userRole === "CSR" || userRole === "CSR Team Lead") {
        return canSeeSupportRoleTab(tabName);
      }
      return true;
    }
    if (tabName === SIDEBAR_TABS.CSR_LEADS) {
      if (!["Admin", "Developer", "CSR", "CSR Team Lead"].includes(userRole)) return false;
      if (userRole === "CSR" || userRole === "CSR Team Lead") {
        return canSeeSupportRoleTab(tabName);
      }
      return true;
    }
    if (tabName === SIDEBAR_TABS.CHAT_SUPPORT_LOG) {
      if (!["Admin", "Developer", "Chat Support", "CSR Team Lead"].includes(userRole)) return false;
      if (userRole === "Chat Support" || userRole === "CSR Team Lead") {
        return canSeeSupportRoleTab(tabName);
      }
      return true;
    }
    if (tabName === SIDEBAR_TABS.CHAT_SUPPORT_ACTIVITY) {
      if (!["Admin", "Developer", "Chat Support", "CSR Team Lead"].includes(userRole)) return false;
      if (userRole === "Chat Support" || userRole === "CSR Team Lead") {
        return canSeeSupportRoleTab(tabName);
      }
      return true;
    }
    if (tabName === SIDEBAR_TABS.APPROVALS) {
      // Team lead only (Admin / Developer can also review).
      return (
        userRole === "CSR Team Lead" ||
        userRole === "Admin" ||
        userRole === "Developer"
      );
    }
    if (tabName === SIDEBAR_TABS.NASE_PRICES) {
      // Visible to all logged-in admin panel roles
      return Boolean(userRole);
    }
    if (tabName === SIDEBAR_TABS.ADMIN_ALERTS) {
      // Same inbox as top-header bell — available to every logged-in role
      return Boolean(userRole);
    }
    if (tabName === SIDEBAR_TABS.SUPPORT) {
      if (userRole === "CSR" || userRole === "CSR Team Lead" || userRole === "Chat Support") {
        return canSeeSupportRoleTab(tabName);
      }
    }
    if (tabName === SIDEBAR_TABS.SALES_CRM) {
      return ["Admin", "Developer", "Sales", "Sales Team Lead"].includes(userRole);
    }
    if (tabName === SIDEBAR_TABS.AGENT_REPORTS) {
      if (
        ![
          "Admin",
          "Developer",
          "Sales Team Lead",
          "CSR Team Lead",
          "CSR",
          "Sales",
          "Chat Support",
        ].includes(userRole)
      ) {
        return false;
      }
      if (SUPPORT_ROLES_WITH_TAB_PERMS.has(userRole)) {
        return canSeeSupportRoleTab(tabName);
      }
      return true;
    }
    if (tabName === SIDEBAR_TABS.CHAT_LIMITS) {
      return userRole === "Admin" || userRole === "Developer";
    }
    if (tabName === SIDEBAR_TABS.LOGIN_DEVICES) {
      return userRole === "Admin" || userRole === "Developer";
    }
    if (tabName === SIDEBAR_TABS.MANUAL_PURCHASE) {
      return userRole === "Developer";
    }
    if (tabName === SIDEBAR_TABS.CSR_MANUAL_SUBSCRIPTION) {
      return userRole === "CSR" || userRole === "CSR Team Lead";
    }
    // Admin: all tabs except restricted ones handled above
    if (userRole === "Admin") {
      return true;
    }
    // Developer: all tabs including Manual Purchase and Support
    if (userRole === "Developer") {
      return true;
    }
    if (userRole === "Author") {
      return [
        SIDEBAR_TABS.DASHBOARD,
        SIDEBAR_TABS.NOTIFICATIONS,
        SIDEBAR_TABS.ADMIN_ALERTS,
        SIDEBAR_TABS.BLOG,
        SIDEBAR_TABS.PROMOTIONAL_MESSAGES,
        SIDEBAR_TABS.USER_PROFILES,
        SIDEBAR_TABS.ADS,
        SIDEBAR_TABS.REFERRALS,
        SIDEBAR_TABS.CONTENT_REPORTS,
        SIDEBAR_TABS.BANNED_USERS,
      ].includes(tabName);
    }
    if (userRole === "Chat Support" || userRole === "CSR" || userRole === "CSR Team Lead") {
      return canSeeSupportRoleTab(tabName);
    }
    if (userRole === "Sales" || userRole === "Sales Team Lead") {
      return [
        SIDEBAR_TABS.DASHBOARD,
        SIDEBAR_TABS.ADMIN_ALERTS,
        SIDEBAR_TABS.AGENT_DESK,
        SIDEBAR_TABS.TEAM_HUB,
      ].includes(tabName);
    }
    return false;
  };

  const handleNavigation = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
    if (isMobile && onClose) {
      onClose();
    }
  };

  const normalizedPath = (location.pathname || "").replace(/\/$/, "") || "/";
  const isExactPath = (path) => normalizedPath === path;
  const canSeeApprovals =
    userRole === "CSR Team Lead" || userRole === "Admin";
  const pendingApprovalsCount = usePendingApprovalsCount(canSeeApprovals);
  const isApprovalsActive =
    location.pathname.includes("/approvals") ||
    (location.pathname.includes("/team-hub") &&
      (location.search || "").includes("section=approvals"));

  const canFetchCsrUnread =
    canAccessAgentDesk(userRole) &&
    (userRole === "CSR" || userRole === "CSR Team Lead" || userRole === "Admin" || userRole === "Developer");
  const canFetchSalesUnread =
    canAccessAgentDesk(userRole) &&
    (userRole === "Sales" || userRole === "Sales Team Lead" || userRole === "Admin" || userRole === "Developer");
  const csrAssignedUnread = useAssignedChatUnreadCount("csr", canFetchCsrUnread);
  const salesAssignedUnread = useAssignedChatUnreadCount(
    "sales",
    canFetchSalesUnread
  );
  const canFetchSupportUnclaimed =
    canAccessAgentDesk(userRole) || canAccessTeamHub(userRole);
  const unclaimedSupportCount = useUnclaimedSupportCount(canFetchSupportUnclaimed);
  const deskBadge =
    (csrAssignedUnread || 0) +
    (salesAssignedUnread || 0) +
    (unclaimedSupportCount || 0);
  const hubBadge = pendingApprovalsCount || 0;
  const isDeskActive = location.pathname.includes("/my-desk");
  const isHubActive = location.pathname.includes("/team-hub");

  const SidebarContent = () => (
    <div
      ref={ref}
      className={`lg:ml-8 px-2 ml-2 lg:px-4 w-full md:w-auto lg:w-90 h-full min-h-[calc(100vh-120px)] bg-black rounded-xl shadow-xl overflow-y-auto no-scrollbar text-white`}
      style={{ overflowX: "visible" }}
    >
      {/* Close button for mobile */}
      {isMobile && onClose && (
        <div className="flex justify-end p-4">
          <button
            onClick={() => onClose()}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>
      )}

      <div className="flex flex-col mt-6 md:mt-10 gap-1 md:gap-0 justify-start">
        {canSeeTab(SIDEBAR_TABS.DASHBOARD) && (
          <div
            onClick={() => handleNavigation("/dashboard")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                            cursor-pointer mb-1 flex items-center gap-3
                            ${location.pathname === "/" || location.pathname === "/dashboard"
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdOutlineDashboard className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.DASHBOARD}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col mt-1 gap-1">
        {canSeeTab(SIDEBAR_TABS.USERS) && (
          <div
            onClick={() => handleNavigation("/topusers")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "topusers"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <CiUser className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.USERS}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.SUBSCRIPTION) && (
          <div
            onClick={() => handleNavigation("/subscription")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${isExactPath("/subscription")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdOutlineSubscriptions className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.SUBSCRIPTION}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.MANUAL_PURCHASE) && (
          <div
            onClick={() => handleNavigation("/manual-purchase")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${isExactPath("/manual-purchase")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdPayment className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.MANUAL_PURCHASE}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.CSR_MANUAL_SUBSCRIPTION) && (
          <div onClick={() => handleNavigation("/csr-manual-subscription")}>
            <div
              className={`w-full px-4 py-3 text-center cursor-pointer mb-1 flex items-center gap-3 ${
                isExactPath("/csr-manual-subscription")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
              }`}
            >
              <div className="md:mr-3">
                <MdPayment className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.CSR_MANUAL_SUBSCRIPTION}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.NOTIFICATIONS) && (
          <div
            onClick={() => handleNavigation("/notifications")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${isExactPath("/notifications")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <IoMdNotificationsOutline className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.NOTIFICATIONS}</p>
              </div>
            </div>
          </div>
        )}
        {canSeeTab(SIDEBAR_TABS.ADMIN_ALERTS) && (
          <div
            onClick={() => handleNavigation("/admin-alerts")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${isExactPath("/admin-alerts")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <IoMdNotificationsOutline className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.ADMIN_ALERTS}</p>
              </div>
            </div>
          </div>
        )}
        {canSeeTab(SIDEBAR_TABS.PROMOTIONAL_MESSAGES) && (
          <div
            onClick={() => handleNavigation("/promotional-messages")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "promotional-messages"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdCampaign className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.PROMOTIONAL_MESSAGES}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.ADS) && (
          <div onClick={() => handleNavigation("/ads")}>
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes("ads")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdAdsClick className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.ADS}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.BLOG) && (
          <div
            onClick={() => handleNavigation("/blogs")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes("blogs")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <FaBlog className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.BLOG}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.USER_PROFILES) && (
          <div
            onClick={() => handleNavigation("/user-profiles")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "/user-profiles"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <LuUsers className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.USER_PROFILES}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.AGENT_DESK) && (
          <div onClick={() => handleNavigation("/my-desk")}>
            <div
              className={`w-full px-4 py-3 text-center cursor-pointer mb-1 flex items-center gap-3 ${
                isDeskActive
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
              }`}
            >
              <div className="md:mr-3">
                <HiOutlineViewGrid className="h-4 w-4" />
              </div>
              <div className="flex-1 flex items-center justify-between gap-2 min-w-0">
                <p className="font-medium text-sm truncate">{SIDEBAR_TABS.AGENT_DESK}</p>
                {deskBadge > 0 && (
                  <span className="shrink-0 text-[10px] font-bold min-w-[18px] h-[18px] px-1.5 rounded-full inline-flex items-center justify-center bg-[#FF2D38] text-white">
                    {deskBadge > 99 ? "99+" : deskBadge}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.TEAM_HUB) && (
          <div onClick={() => handleNavigation("/team-hub")}>
            <div
              className={`w-full px-4 py-3 text-center cursor-pointer mb-1 flex items-center gap-3 ${
                isHubActive
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
              }`}
            >
              <div className="md:mr-3">
                <MdGroups className="h-4 w-4" />
              </div>
              <div className="flex-1 flex items-center justify-between gap-2 min-w-0">
                <p className="font-medium text-sm truncate">{SIDEBAR_TABS.TEAM_HUB}</p>
                {hubBadge > 0 && (
                  <span
                    className={`shrink-0 text-[10px] font-bold min-w-[18px] h-[18px] px-1.5 rounded-full inline-flex items-center justify-center ${
                      isApprovalsActive ? "bg-amber-500 text-white" : "bg-amber-400 text-black"
                    }`}
                  >
                    {hubBadge > 99 ? "99+" : hubBadge}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.SUPPORT_TEAMS) && (
          <div onClick={() => handleNavigation("/support-teams")}>
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                location.pathname.includes("/support-teams")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <HiUsers className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.SUPPORT_TEAMS}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.NASE_PRICES) && (
          <div onClick={() => handleNavigation("/nase-prices")}>
            <div
              className={`w-full px-4 py-3 text-center cursor-pointer mb-1 flex items-center gap-3 ${
                location.pathname.includes("/nase-prices")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
              }`}
            >
              <div className="md:mr-3">
                <MdOutlineAttachMoney className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.NASE_PRICES}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.CONTENT_REPORTS) && (
          <div onClick={() => handleNavigation("/content-reports")}>
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                location.pathname.includes("/content-reports")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdOutlineFlag className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.CONTENT_REPORTS}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.BANNED_USERS) && (
          <div onClick={() => handleNavigation("/community-banned-users")}>
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                location.pathname.includes("/community-banned-users")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdOutlineBlock className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.BANNED_USERS}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.CHAT_LIMITS) && (
          <div onClick={() => handleNavigation("/chat-limits")}>
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                location.pathname.includes("/chat-limits")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdOutlineChat className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.CHAT_LIMITS}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.LOGIN_DEVICES) && (
          <div onClick={() => handleNavigation("/login-devices")}>
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                location.pathname.includes("/login-devices")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdDevices className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.LOGIN_DEVICES}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.MATCH_USER) && (
          <div
            onClick={() => handleNavigation("/match-user")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "/match-user"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <FaUserFriends className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.MATCH_USER}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.APP_VERSION_CONTROL) && (
          <div
            onClick={() => handleNavigation("/app-version")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "/app-version"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdSystemUpdateAlt className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.APP_VERSION_CONTROL}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.ADMIN_PANEL_USERS) && (
          <div
            onClick={() => handleNavigation("/admin-panel-users")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "/admin-panel-users"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <HiUsers className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.ADMIN_PANEL_USERS}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.BOOST_HISTORY) && (
          <div
            onClick={() => handleNavigation("/boost-history")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "/boost-history"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <FaRocket className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.BOOST_HISTORY}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.REFERRALS) && (
          <div onClick={() => handleNavigation("/referrals")}>
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes("/referrals")
                 ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdCardGiftcard className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.REFERRALS}</p>
              </div>
            </div>
          </div>
        )}


        {canSeeTab(SIDEBAR_TABS.DUMMY_USERS) && (
          <div
            onClick={() => handleNavigation("/dummy-users")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "/dummy-users"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <FiUserPlus className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.DUMMY_USERS}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );





  // Client hydration + first permissions fetch for support roles
  if (!isClient || !accessReady) {
    return <SidebarSkeleton items={SUPPORT_ROLES_WITH_TAB_PERMS.has(userRole) ? 8 : 14} />;
  }

  return <SidebarContent />;
});

export default Sidebar;